// A prepaid pool: what was bought, what it covers, and where it stands.
//
// The POSITION is not stored. Consumption is a sum over the covered term lines'
// quantities for the period, and the allocation across them is pro-rata with
// the whole-unit remainder distributed by largest remainder — so it is
// evaluated like every other figure here, and it arrives on the statement line
// the pool covers. That is why this section takes the positions from the
// statement rather than reading a column: a stored position would be a fifth
// copy of a figure the evaluator already knows.
//
// THE OPENING BALANCE IS THREE ROWS, NOT ONE NUMBER. What was bought is what
// the paper sold. What carried in is COMPUTED from the earlier periods' facts.
// An adjustment is what somebody STATED, and it carries the note saying why.
// The three add up to the opening balance, and they stay apart on the page —
// a stated figure merged into a computed one is a balance nobody can check.
//
// The partner page shows the same block with NO positions, and that is correct
// rather than a degradation: a pool bought on one deal may cover lines on
// several, so there is no single statement to read it against. What the partner
// page answers is "what pools does this relationship hold".
//
// READ-ONLY. A pool is written by the CLI (`crm prepaid-entitlements`), the
// same way a tenant binding is, and for the same reason: what it covers is a
// list of term-line ids, and a picker over them is a form nobody has asked for
// yet.

import type { ReactNode } from "react";
import type { Currency, DealStatementPrepaid, PrepaidEntitlement } from "@crm/shared";
import styles from "./PrepaidEntitlementsSection.module.css";
import { Card, CardHeader } from "../../design/Card.js";
import { EmptyRow, Table, Td, Th } from "../../design/Table.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { usePrepaidEntitlements, type PrepaidScope } from "../../api/queries.js";
import { formatMonth } from "../../format/calendarDate.js";
import { formatCount, formatQuantity } from "../../format/money.js";
import { factSourceLabel } from "../../format/labels.js";
import { signed } from "./StatementPanel.js";

export interface PrepaidEntitlementsSectionProps {
  scope: PrepaidScope;
  /**
   * Where each pool stands, by entitlement id, from the period's statement.
   * Empty on the partner page — see the note above.
   */
  positions: ReadonlyMap<string, DealStatementPrepaid>;
  /** Named in the empty state, so it says what this deal or partner is. */
  ownerName: string;
  currency: Currency;
}

export function PrepaidEntitlementsSection({
  scope,
  positions,
  ownerName,
}: PrepaidEntitlementsSectionProps): ReactNode {
  const pools = usePrepaidEntitlements(scope);
  const showPosition = positions.size > 0;

  return (
    <Card>
      <CardHeader
        title="Prepaid entitlements"
        description="A block bought up front, and the term lines it covers. Consumption and the allocation across those lines are evaluated, never stored — so deleting every cache brings the same position back."
      />
      <QueryBoundary query={pools} label="the prepaid pools">
        {(page) => (
          <Table label={`Prepaid entitlements for ${ownerName}`}>
            <thead>
              <tr>
                <Th>Covers</Th>
                <Th>Window</Th>
                <Th numeric>Block</Th>
                {showPosition && <Th numeric>Opening</Th>}
                {showPosition && <Th numeric>Consumed</Th>}
                {showPosition && <Th numeric>Allocated here</Th>}
                {showPosition && <Th numeric>Remaining</Th>}
              </tr>
            </thead>
            <tbody>
              {page.items.length === 0 ? (
                <EmptyRow
                  colSpan={showPosition ? 7 : 3}
                  title="No prepaid entitlements"
                  description={`Nothing is bought up front for ${ownerName}. Every covered clause bills what its facts come to, period by period.`}
                />
              ) : (
                page.items.map((pool) => (
                  <PoolRow
                    key={pool.id}
                    pool={pool}
                    position={positions.get(pool.id)}
                    showPosition={showPosition}
                  />
                ))
              )}
            </tbody>
          </Table>
        )}
      </QueryBoundary>
    </Card>
  );
}

function PoolRow({
  pool,
  position,
  showPosition,
}: {
  pool: PrepaidEntitlement;
  position: DealStatementPrepaid | undefined;
  showPosition: boolean;
}): ReactNode {
  const covered = pool.covered_term_line_ids.length;

  return (
    <tr>
      <Td strong>
        {`${formatCount(covered)} term line${covered === 1 ? "" : "s"}`}
        {pool.note !== null && <span className={styles.note}>{pool.note}</span>}
        {pool.carry_over === "unused_rolls_forward" && (
          <span className={styles.note}>What is left rolls into the next period.</span>
        )}
        {/* The opening balance, broken into the three things it is made of. On
            the page rather than folded away, because the question a person asks
            of a pool is "why is the balance what it is", and one number cannot
            answer it. */}
        {position !== undefined && (
          <span className={styles.opening}>
            <span className={styles.openingPart}>
              {`${formatQuantity(position.entitlement_quantity)} bought`}
            </span>
            <span className={styles.openingPart}>
              {`${formatQuantity(position.carried_in_quantity)} carried in`}
            </span>
            {position.adjustments.length === 0 ? (
              <span className={styles.openingPart}>nothing adjusted</span>
            ) : (
              position.adjustments.map((adjustment, index) => (
                <span
                  key={`${adjustment.recorded_by}-${String(index)}`}
                  className={styles.openingPart}
                >
                  {`${signed(adjustment.quantity)} adjusted · ` +
                    `${factSourceLabel(adjustment.source).toLowerCase()}` +
                    (adjustment.note === null ? "" : ` — ${adjustment.note}`)}
                </span>
              ))
            )}
          </span>
        )}
      </Td>
      <Td muted nowrap>
        {`${formatMonth(pool.period_start)} – ${formatMonth(pool.period_end)}`}
      </Td>
      <Td numeric mono>
        {`${formatQuantity(pool.quantity)} ${pool.unit}`}
      </Td>
      {showPosition && (
        <Td numeric mono>
          {position === undefined ? "—" : formatQuantity(position.opening_quantity)}
        </Td>
      )}
      {showPosition && (
        <Td numeric mono muted>
          {position === undefined ? "—" : formatQuantity(position.consumed_quantity)}
        </Td>
      )}
      {showPosition && (
        <Td numeric mono>
          {position === undefined ? "—" : formatQuantity(position.allocated_quantity)}
        </Td>
      )}
      {showPosition && (
        <Td numeric mono strong>
          {position === undefined ? (
            "—"
          ) : position.overage_quantity > 0 ? (
            <span
              className={styles.over}
            >{`${formatQuantity(position.overage_quantity)} over`}</span>
          ) : (
            formatQuantity(position.remaining_quantity)
          )}
        </Td>
      )}
    </tr>
  );
}
